import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePublishAd } from '@/features/meta-ads/api';
import { readDraftPromotionError } from '@/features/meta-ads/api/launch-ad/draft-promotion';
import { usePublishDraftPromotion } from '@/features/meta-ads/api/launch-ad/use-publish-draft-promotion';
import type { Ad } from '@/features/meta-ads/api/types';
import { useState } from 'react';

/**
 * An ad that has never reached Meta and can be sent there from the ads list:
 * a draft, or an ad whose launch failed before Meta accepted it. The server's
 * rule (`publishAd`) is the same — no `metaAdId`, status `draft` or `error`.
 *
 * Without this, an `error` ad was a dead end: the row said "try again" and
 * offered nothing to try, and editing it is refused while it is in that state.
 */
export function canLaunchAd(ad: Ad): boolean {
  return (
    !ad.metaAdId &&
    !ad.isImported &&
    (ad.status === 'draft' || ad.status === 'error')
  );
}

/**
 * Confirm BEFORE launching: publishing turns the ad's campaign and ad set on,
 * so it starts spending once Meta approves it.
 *
 * One per table, not per row: a single dialog and mutation per ad means the
 * row menu and the sync-error button cannot start two publishes at once.
 *
 * A failed attempt shows its reason here, from the response, so the owner
 * reads it without closing the dialog. The server also saves most failures on
 * the row, but deliberately not an unpublished promotion (a precondition, not
 * a failed launch), and this is the only place that one shows. For that
 * case the dialog offers to publish the promotion with the ad, as the ad
 * wizard and Claire's preview card do — otherwise it was a dead end that sent
 * the owner off to the Offers page.
 */
export function LaunchAdDialog({
  ad,
  open,
  onOpenChange,
}: {
  ad: Ad;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const {
    executeAsync: publishAsync,
    isExecuting,
    error,
  } = usePublishAd(true, false);
  const { publishAsync: publishPromotionAsync, isPublishing } =
    usePublishDraftPromotion();
  const [promotionError, setPromotionError] = useState<string | null>(null);
  const draftPromotion = readDraftPromotionError(error);
  const isBusy = isExecuting || isPublishing;
  const retry = ad.status === 'error';

  const launch = async () => {
    try {
      await publishAsync(ad.id);
      onOpenChange(false);
    } catch {
      // The reason renders above; the dialog stays for another try.
    }
  };

  /** The owner saw the draft promotion named and chose to publish it too. */
  const publishPromotionAndLaunch = async (offerId: string) => {
    setPromotionError(null);
    try {
      await publishPromotionAsync(offerId);
    } catch (publishError) {
      setPromotionError(
        publishError instanceof Error && publishError.message
          ? publishError.message
          : 'Failed to publish the promotion'
      );
      return;
    }
    await launch();
  };

  return (
    <AlertDialog
      open={open}
      // Escape or an outside press must not drop a launch that is under way.
      onOpenChange={(next) => {
        if (!isBusy) onOpenChange(next);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {retry ? 'Retry launching' : 'Launch'} &ldquo;{ad.name}&rdquo;?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This sends the ad to Meta and switches its campaign and ad set on,
            so it starts spending your budget once Meta approves it. If it still
            cannot launch, the reason shows here.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {(promotionError ?? error) && (
          <p
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {promotionError ?? error?.message}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isBusy}>Cancel</AlertDialogCancel>
          {draftPromotion ? (
            <AlertDialogAction
              disabled={isBusy}
              onClick={(event) => {
                event.preventDefault();
                void publishPromotionAndLaunch(draftPromotion.offerId);
              }}
            >
              {isBusy ? 'Publishing...' : 'Publish promotion and launch'}
            </AlertDialogAction>
          ) : (
            <AlertDialogAction
              disabled={isBusy}
              onClick={(event) => {
                event.preventDefault();
                void launch();
              }}
            >
              {isExecuting
                ? 'Launching...'
                : error || retry
                  ? 'Retry launch'
                  : 'Launch ad'}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
